import React, { forwardRef, useLayoutEffect, useRef } from 'react';
import { Typography } from '@mui/material';

/** Short UI copy: preserve its normal size, then fit the actual available width. */
const SingleLineText = forwardRef(function SingleLineText({ children, sx, minFontSize = 12, style, ...props }, forwardedRef) {
    const elementRef = useRef(null);
    const fitRef = useRef(null);
    useLayoutEffect(() => {
        const element = elementRef.current;
        if (!element) return undefined;
        let active = true;
        const fit = () => {
            if (!active || !element.isConnected || element.clientWidth === 0) return;
            // Remove only our measurement override, retaining the caller's base size.
            element.style.fontSize = style?.fontSize == null ? '' : (typeof style.fontSize === 'number' ? `${style.fontSize}px` : style.fontSize);
            element.style.whiteSpace = style?.whiteSpace ?? '';
            element.style.overflowWrap = style?.overflowWrap ?? '';
            const css = getComputedStyle(element);
            const baseSize = parseFloat(css.fontSize);
            const available = element.getBoundingClientRect().width
                - parseFloat(css.paddingLeft) - parseFloat(css.paddingRight)
                - parseFloat(css.borderLeftWidth) - parseFloat(css.borderRightWidth);
            const range = document.createRange();
            range.selectNodeContents(element);
            if (!range.getBoundingClientRect) return;
            const naturalWidth = range.getBoundingClientRect().width;
            if (available > 0 && naturalWidth > available) {
                const minimum = Math.min(minFontSize, baseSize);
                let size = Math.max(minimum, Math.floor(baseSize * (available - 1) / naturalWidth * 10) / 10);
                element.style.fontSize = `${size}px`;
                // Glyph advances and fixed letter spacing need not scale linearly with font size.
                // Verify the rendered width rather than trusting the first proportional estimate.
                let actualWidth = range.getBoundingClientRect().width;
                for (let attempt = 0; attempt < 8 && actualWidth > available && size > minimum; attempt += 1) {
                    size = Math.max(minimum, Math.min(size - 0.1,
                        Math.floor(size * (available - 1) / actualWidth * 10) / 10));
                    element.style.fontSize = `${size}px`;
                    actualWidth = range.getBoundingClientRect().width;
                }
                // Preserve readable minimum sizing when a short label cannot fit one line.
                if (actualWidth > available) {
                    element.style.whiteSpace = 'normal';
                    element.style.overflowWrap = 'anywhere';
                }
            }
        };
        fitRef.current = fit;
        // ResizeObserver delivery must not synchronously resize the observed text.
        // Font fitting changes height too; only available-width changes need a refit.
        let measuredWidth;
        let resizeFrame;
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(entries => {
            const width = entries[0]?.contentRect.width;
            if (width == null || width === measuredWidth) return;
            measuredWidth = width;
            cancelAnimationFrame(resizeFrame);
            resizeFrame = requestAnimationFrame(fit);
        });
        observer?.observe(element);
        // Root font scaling can change rem sizes without changing available width.
        const styleObserver = typeof MutationObserver === 'undefined' ? null : new MutationObserver(() => {
            cancelAnimationFrame(resizeFrame);
            resizeFrame = requestAnimationFrame(fit);
        });
        styleObserver?.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
        if (document.head) styleObserver?.observe(document.head, { childList: true, subtree: true, characterData: true });
        document.fonts?.ready.then(fit);
        document.fonts?.addEventListener('loadingdone', fit);
        return () => {
            active = false;
            fitRef.current = null;
            observer?.disconnect();
            styleObserver?.disconnect();
            cancelAnimationFrame(resizeFrame);
            document.fonts?.removeEventListener('loadingdone', fit);
        };
    }, [minFontSize, style?.fontSize, style?.whiteSpace, style?.overflowWrap]);
    // Remeasure after text, selected weight, or caller styling changes as well.
    useLayoutEffect(() => { fitRef.current?.(); });

    return <Typography
        {...props}
        ref={element => {
            elementRef.current = element;
            if (typeof forwardedRef === 'function') forwardedRef(element);
            else if (forwardedRef) forwardedRef.current = element;
        }}
        data-single-line="true"
        style={style}
        sx={[
            { minWidth: 0, whiteSpace: 'nowrap' },
            ...(Array.isArray(sx) ? sx : [sx]),
        ]}
    >{children}</Typography>;
});

export default SingleLineText;
